import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { PlanDuration } from './PlanDuration';
import { DurationPicker } from './DurationPicker';
import { AdminSidebarLayout } from './SidebarLayout';
import { SearchBar, Pagination, FilterSelect } from './ListControls';
import { CategoryCombobox } from './CategoryCombobox';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from './ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter
} from './ui/dialog';
import {
  Plus, Edit, Trash2, Loader2, Save, ShoppingBag, CheckCircle, Star, X, Download, Award
} from 'lucide-react';
import { toast } from 'sonner';

export function AdminServicesManagement() {
  const { api } = useAuth();
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [skip, setSkip] = useState(0);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(15);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category_id: '',
    regular_price: '',
    member_price: '',
    discount_percentage: '',
    image_url: '',
    is_active: true,
    order: 0
  });

  useEffect(() => {
    fetchData();
  }, [skip, search, statusFilter, categoryFilter, limit]);

  useEffect(() => { setSkip(0); }, [search, statusFilter, categoryFilter, limit]);

  const fetchData = async () => {
    try {
      let url = `/api/admin/services?skip=${skip}&limit=${limit}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      if (statusFilter) url += `&status=${statusFilter}`;
      if (categoryFilter) url += `&category=${encodeURIComponent(categoryFilter)}`;
      const [servicesRes, categoriesRes] = await Promise.all([
        api.get(url),
        api.get('/api/admin/service-categories')
      ]);
      setServices(servicesRes.data.services || []);
      setTotal(servicesRes.data.total || 0);
      setCategories(categoriesRes.data.categories || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const openDialog = (service = null) => {
    if (service) {
      setEditingService(service);
      setFormData({
        name: service.name,
        description: service.description,
        category_id: service.category_id || '',
        regular_price: service.regular_price,
        member_price: service.member_price,
        discount_percentage: service.discount_percentage,
        image_url: service.image_url || '',
        is_active: service.is_active,
        order: service.order || 0
      });
    } else {
      setEditingService(null);
      setFormData({
        name: '',
        description: '',
        category_id: '',
        regular_price: '',
        member_price: '',
        discount_percentage: '',
        image_url: '',
        is_active: true,
        order: 0
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      const data = {
        ...formData,
        regular_price: parseFloat(formData.regular_price),
        member_price: parseFloat(formData.member_price),
        discount_percentage: parseFloat(formData.discount_percentage) || 0,
        order: parseInt(formData.order) || 0
      };
      
      if (editingService) {
        await api.put(`/api/admin/services/${editingService.id}`, data);
        toast.success('Service updated');
      } else {
        await api.post('/api/admin/services/create', data);
        toast.success('Service created');
      }
      
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save service');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to deactivate this service?')) return;
    
    try {
      await api.delete(`/api/admin/services/${id}`);
      toast.success('Service deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete service');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency: 'PHP',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <AdminSidebarLayout>
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#00E5FF]" />
        </div>
      </AdminSidebarLayout>
    );
  }

  return (
    <AdminSidebarLayout>
      <div className="p-4 sm:p-6 lg:p-8">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6 sm:mb-8">
            <div>
              <h1 className="font-outfit text-2xl sm:text-3xl font-semibold text-white">Services Management</h1>
              <p className="text-[#A0B4CC] mt-1">Create and manage platform services</p>
            </div>
            <Button onClick={() => openDialog()} className="btn-primary" data-testid="add-service-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Service
            </Button>
          </div>

          <div className="flex flex-wrap items-center gap-3 mb-6">
            <div className="flex-1 min-w-[200px]">
              <SearchBar value={search} onChange={setSearch} placeholder="Search services..." />
            </div>
            <FilterSelect value={statusFilter} onChange={setStatusFilter} placeholder="All Statuses" options={[{ value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }]} />
            {categories.length > 0 && (
              <FilterSelect value={categoryFilter} onChange={setCategoryFilter} placeholder="All Categories" options={categories.map(c => ({ value: c.id, label: c.name }))} />
            )}
          </div>

          <Card className="bg-[#041A3B] border-white/10">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10">
                      <TableHead className="text-[#A0B4CC]">Service</TableHead>
                      <TableHead className="text-[#A0B4CC]">Regular Price</TableHead>
                      <TableHead className="text-[#A0B4CC]">Member Price</TableHead>
                      <TableHead className="text-[#A0B4CC]">Discount</TableHead>
                      <TableHead className="text-[#A0B4CC]">Status</TableHead>
                      <TableHead className="text-[#A0B4CC] text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {services.map((service) => (
                      <TableRow key={service.id} className="border-white/5 hover:bg-white/5">
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-[#00E5FF]/10 flex items-center justify-center">
                              <ShoppingBag className="w-5 h-5 text-[#00E5FF]" />
                            </div>
                            <div>
                              <p className="text-white font-medium">{service.name}</p>
                              <p className="text-[#5C7599] text-xs line-clamp-1">{service.description}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-white">{formatCurrency(service.regular_price)}</TableCell>
                        <TableCell className="text-[#00E676]">{formatCurrency(service.member_price)}</TableCell>
                        <TableCell>
                          <Badge className="bg-[#00E5FF]/20 text-[#00E5FF] border-0">
                            {service.discount_percentage}%
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={service.is_active 
                            ? 'bg-[#00E676]/20 text-[#00E676] border-0' 
                            : 'bg-[#5C7599]/20 text-[#5C7599] border-0'
                          }>
                            {service.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              onClick={() => openDialog(service)}
                              className="text-[#A0B4CC] hover:text-white"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              onClick={() => handleDelete(service.id)}
                              className="text-[#FF1744] hover:text-[#FF1744]"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              
              {services.length === 0 && (
                <div className="py-12 text-center">
                  <ShoppingBag className="w-12 h-12 text-[#5C7599] mx-auto mb-4" />
                  <p className="text-[#A0B4CC]">No services yet. Add your first service!</p>
                </div>
              )}
            </CardContent>
          </Card>

          <Pagination skip={skip} limit={limit} total={total} onPageChange={setSkip} onLimitChange={(v) => { setLimit(v); setSkip(0); }} />

          {/* Service Dialog */}
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogContent className="bg-[#041A3B] border-white/10 max-w-lg">
              <DialogHeader>
                <DialogTitle className="text-white">
                  {editingService ? 'Edit Service' : 'Add New Service'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-white">Service Name</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="bg-[#000B18] border-white/20 text-white"
                    placeholder="Business Consulting"
                    data-testid="service-name-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-white">Description</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    required
                    className="bg-[#000B18] border-white/20 text-white"
                    placeholder="Service description..."
                    rows={3}
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-white">Category</Label>
                  <CategoryCombobox
                    api={api}
                    itemType="service"
                    value={formData.category_id}
                    onChange={(catId) => setFormData({ ...formData, category_id: catId })}
                    placeholder="Pick or search a category…"
                    testId="admin-content-svc-category"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-white">Regular Price (PHP)</Label>
                    <Input
                      type="number"
                      value={formData.regular_price}
                      onChange={(e) => setFormData({ ...formData, regular_price: e.target.value })}
                      required
                      className="bg-[#000B18] border-white/20 text-white"
                      placeholder="5000"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white">Member Price (PHP)</Label>
                    <Input
                      type="number"
                      value={formData.member_price}
                      onChange={(e) => setFormData({ ...formData, member_price: e.target.value })}
                      required
                      className="bg-[#000B18] border-white/20 text-white"
                      placeholder="3500"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-white">Discount %</Label>
                    <Input
                      type="number"
                      value={formData.discount_percentage}
                      onChange={(e) => setFormData({ ...formData, discount_percentage: e.target.value })}
                      className="bg-[#000B18] border-white/20 text-white"
                      placeholder="30"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white">Display Order</Label>
                    <Input
                      type="number"
                      value={formData.order}
                      onChange={(e) => setFormData({ ...formData, order: e.target.value })}
                      className="bg-[#000B18] border-white/20 text-white"
                      placeholder="0"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-white">Image URL (Optional)</Label>
                  <Input
                    value={formData.image_url}
                    onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                    className="bg-[#000B18] border-white/20 text-white"
                    placeholder="https://..."
                  />
                </div>
                
                <div className="flex items-center justify-between p-3 rounded-lg bg-[#000B18]">
                  <Label className="text-white">Active</Label>
                  <Switch
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                  />
                </div>

                <DialogFooter>
                  <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)} className="text-[#A0B4CC]">
                    Cancel
                  </Button>
                  <Button type="submit" disabled={saving} className="btn-primary" data-testid="save-service-btn">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Save
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
      </div>
    </AdminSidebarLayout>
  );
}

export function AdminPlansManagement() {
  const { api } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [skip, setSkip] = useState(0);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(15);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    features: [''],
    duration_days: 30,
    is_active: true,
    is_popular: false,
    order: 0
  });

  useEffect(() => {
    fetchPlans();
  }, [skip, search, statusFilter, limit]);

  useEffect(() => { setSkip(0); }, [search, statusFilter, limit]);

  const fetchPlans = async () => {
    try {
      let url = `/api/admin/plans?skip=${skip}&limit=${limit}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      const { data } = await api.get(url);
      setPlans(data.plans || []);
      setTotal(data.total || 0);
    } catch (error) {
      console.error('Failed to fetch plans:', error);
    } finally {
      setLoading(false);
    }
  };

  const openDialog = (plan = null) => {
    if (plan) {
      setEditingPlan(plan);
      setFormData({
        name: plan.name,
        description: plan.description,
        price: plan.price,
        features: plan.features || [''],
        duration_days: plan.duration_days,
        is_active: plan.is_active,
        is_popular: plan.is_popular || false,
        order: plan.order || 0
      });
    } else {
      setEditingPlan(null);
      setFormData({
        name: '',
        description: '',
        price: '',
        features: [''],
        duration_days: 30,
        is_active: true,
        is_popular: false,
        order: 0
      });
    }
    setDialogOpen(true);
  };

  const addFeature = () => {
    setFormData({ ...formData, features: [...formData.features, ''] });
  };

  const updateFeature = (index, value) => {
    const newFeatures = [...formData.features];
    newFeatures[index] = value;
    setFormData({ ...formData, features: newFeatures });
  };

  const removeFeature = (index) => {
    const newFeatures = formData.features.filter((_, i) => i !== index);
    setFormData({ ...formData, features: newFeatures });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      const data = {
        ...formData,
        price: parseFloat(formData.price),
        duration_days: parseInt(formData.duration_days),
        features: formData.features.filter(f => f.trim() !== ''),
        order: parseInt(formData.order) || 0
      };
      
      if (editingPlan) {
        await api.put(`/api/admin/plans/${editingPlan.id}`, data);
        toast.success('Plan updated');
      } else {
        await api.post('/api/admin/plans/create', data);
        toast.success('Plan created');
      }
      
      setDialogOpen(false);
      fetchPlans();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save plan');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to deactivate this plan?')) return;
    
    try {
      await api.delete(`/api/admin/plans/${id}`);
      toast.success('Plan deleted');
      fetchPlans();
    } catch (error) {
      toast.error('Failed to delete plan');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency: 'PHP',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <AdminSidebarLayout>
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#00E5FF]" />
        </div>
      </AdminSidebarLayout>
    );
  }

  return (
    <AdminSidebarLayout>
      <div className="p-4 sm:p-6 lg:p-8">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6 sm:mb-8">
            <div>
              <h1 className="font-outfit text-2xl sm:text-3xl font-semibold text-white">Subscription Plans</h1>
              <p className="text-[#A0B4CC] mt-1">Create and manage membership plans</p>
            </div>
            <Button onClick={() => openDialog()} className="btn-primary" data-testid="add-plan-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Plan
            </Button>
          </div>

          <div className="flex flex-wrap items-center gap-3 mb-6">
            <div className="flex-1 min-w-[200px]">
              <SearchBar value={search} onChange={setSearch} placeholder="Search plans..." />
            </div>
            <FilterSelect value={statusFilter} onChange={setStatusFilter} placeholder="All Statuses" options={[{ value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }]} />
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {plans.map((plan) => (
              <Card key={plan.id} className={`bg-[#041A3B] border-white/10 relative ${plan.is_popular ? 'ring-2 ring-[#00E5FF]' : ''}`}>
                {plan.is_popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge className="bg-[#00E5FF] text-[#000B18]">
                      <Star className="w-3 h-3 mr-1" />
                      Popular
                    </Badge>
                  </div>
                )}
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="font-outfit text-xl font-semibold text-white">{plan.name}</h3>
                      <p className="text-[#5C7599] text-sm">{plan.description}</p>
                    </div>
                    <Badge className={plan.is_active ? 'bg-[#00E676]/20 text-[#00E676]' : 'bg-[#5C7599]/20 text-[#5C7599]'}>
                      {plan.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                  
                  <div className="mb-4">
                    <span className="font-outfit text-3xl font-bold text-white">{formatCurrency(plan.price)}</span>
                    <span className="text-[#5C7599] text-sm">/ <PlanDuration days={plan.duration_days} /></span>
                    {(!plan.duration_days || plan.duration_days <= 0) && (
                      <span className="ml-2 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider bg-[#FFB300]/15 text-[#FFB300] border border-[#FFB300]/30 align-middle">∞ Lifetime</span>
                    )}
                  </div>

                  {/* Stock-Certificate cap derived from the plan's tier.
                      Surfaces the (cap, chunks) the member can earn so admins
                      don't need to leave the Plans page to verify cert rules. */}
                  <div
                    className="mb-4 flex items-center gap-2 px-3 py-2 rounded-md bg-[#FFEA00]/5 border border-[#FFEA00]/20"
                    data-testid={`plan-cert-cap-${plan.id}`}
                  >
                    <Award className="w-4 h-4 text-[#FFEA00] flex-shrink-0" />
                    {plan.cert_cap_usd > 0 ? (
                      <div className="text-xs leading-tight">
                        <span className="text-[#FFEA00] font-semibold">
                          Cert cap: ${Number(plan.cert_cap_usd).toLocaleString()}
                        </span>
                        <span className="text-[#A0B4CC]">
                          {' '}· {plan.cert_chunks} × ${plan.cert_chunk_usd}
                        </span>
                      </div>
                    ) : (
                      <span className="text-xs text-[#5C7599]">No stock certificate on this tier</span>
                    )}
                  </div>
                  
                  <ul className="space-y-2 mb-6">
                    {plan.features?.slice(0, 4).map((feature, i) => (
                      <li key={`${feature}-${i}`} className="flex items-center gap-2 text-[#A0B4CC] text-sm">
                        <CheckCircle className="w-4 h-4 text-[#00E676]" />
                        {feature}
                      </li>
                    ))}
                    {plan.features?.length > 4 && (
                      <li className="text-[#5C7599] text-sm">+{plan.features.length - 4} more features</li>
                    )}
                  </ul>
                  
                  <div className="flex gap-2">
                    <Button 
                      size="sm" 
                      onClick={() => openDialog(plan)}
                      className="flex-1 btn-secondary"
                    >
                      <Edit className="w-4 h-4 mr-1" />
                      Edit
                    </Button>
                    <Button 
                      size="sm" 
                      variant="ghost"
                      onClick={() => handleDelete(plan.id)}
                      className="text-[#FF1744]"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {plans.length === 0 && (
            <Card className="bg-[#041A3B] border-white/10">
              <CardContent className="py-12 text-center">
                <Star className="w-12 h-12 text-[#5C7599] mx-auto mb-4" />
                <p className="text-[#A0B4CC]">No plans yet. Add your first subscription plan!</p>
              </CardContent>
            </Card>
          )}

          <Pagination skip={skip} limit={limit} total={total} onPageChange={setSkip} onLimitChange={(v) => { setLimit(v); setSkip(0); }} />

          {/* Plan Dialog */}
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogContent className="bg-[#041A3B] border-white/10 max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-white">
                  {editingPlan ? 'Edit Plan' : 'Add New Plan'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-white">Plan Name</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      className="bg-[#000B18] border-white/20 text-white"
                      placeholder="Professional"
                      data-testid="plan-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white">Price (PHP)</Label>
                    <Input
                      type="number"
                      value={formData.price}
                      onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                      required
                      className="bg-[#000B18] border-white/20 text-white"
                      placeholder="15000"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-white">Description</Label>
                  <Input
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    required
                    className="bg-[#000B18] border-white/20 text-white"
                    placeholder="For serious members"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-white">Membership Duration</Label>
                    <DurationPicker
                      days={parseInt(formData.duration_days, 10) || 0}
                      onChange={(d) => setFormData({ ...formData, duration_days: d })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white">Display Order</Label>
                    <Input
                      type="number"
                      value={formData.order}
                      onChange={(e) => setFormData({ ...formData, order: e.target.value })}
                      className="bg-[#000B18] border-white/20 text-white"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-white">Features</Label>
                    <Button type="button" size="sm" variant="ghost" onClick={addFeature} className="text-[#00E5FF]">
                      <Plus className="w-4 h-4 mr-1" />
                      Add
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {formData.features.map((feature, index) => (
                      <div key={`feature-input-${index}`} className="flex gap-2">
                        <Input
                          value={feature}
                          onChange={(e) => updateFeature(index, e.target.value)}
                          className="bg-[#000B18] border-white/20 text-white flex-1"
                          placeholder="Feature description"
                        />
                        {formData.features.length > 1 && (
                          <Button 
                            type="button" 
                            size="sm" 
                            variant="ghost" 
                            onClick={() => removeFeature(index)}
                            className="text-[#FF1744]"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="flex items-center justify-between p-3 rounded-lg bg-[#000B18]">
                  <Label className="text-white">Mark as Popular</Label>
                  <Switch
                    checked={formData.is_popular}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_popular: checked })}
                  />
                </div>
                
                <div className="flex items-center justify-between p-3 rounded-lg bg-[#000B18]">
                  <Label className="text-white">Active</Label>
                  <Switch
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                  />
                </div>

                <DialogFooter>
                  <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)} className="text-[#A0B4CC]">
                    Cancel
                  </Button>
                  <Button type="submit" disabled={saving} className="btn-primary" data-testid="save-plan-btn">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Save
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
      </div>
    </AdminSidebarLayout>
  );
}
